import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { supabase } from '../lib/supabase'
import { usePageTitle } from '../lib/usePageTitle'

interface Group {
  id: string
  name: string
  slug: string
  invite_token: string
  owner_id: string
  is_default: boolean
}

interface SearchHit {
  id: string
  name: string
  slug: string
  member_count: number
  is_member: boolean
  has_pending: boolean
}

interface MyApplication {
  id: string
  group_name: string
  status: string
  created_at: string
}

interface IncomingApplication {
  id: string
  group_id: string
  group_name: string
  applicant_name: string
  message: string | null
  created_at: string
}

const APPLICATION_STATUS: Record<string, string> = {
  pending: 'Väntar på svar',
  approved: 'Godkänd',
  rejected: 'Avslagen',
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
}

export function Groups() {
  usePageTitle('Gäng')
  const [groups, setGroups] = useState<Group[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  const [code, setCode] = useState('')

  const [query, setQuery] = useState('')
  const [hits, setHits] = useState<SearchHit[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [applied, setApplied] = useState<string | null>(null)
  const [myApplications, setMyApplications] = useState<MyApplication[]>([])
  const [incoming, setIncoming] = useState<IncomingApplication[]>([])

  const load = useCallback(async () => {
    const [g, mine, inbox] = await Promise.all([
      supabase
        .from('groups')
        .select('id, name, slug, invite_token, owner_id, is_default')
        .order('name'),
      supabase.rpc('my_group_applications'),
      supabase.rpc('incoming_group_applications'),
    ])

    if (g.error) setError(g.error.message)
    else setGroups((g.data as Group[]) ?? [])
    if (!mine.error) setMyApplications((mine.data as MyApplication[]) ?? [])
    if (!inbox.error) setIncoming((inbox.data as IncomingApplication[]) ?? [])
    setLoading(false)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function search(e: FormEvent) {
    e.preventDefault()
    setSearching(true)
    setError(null)
    const { data, error } = await supabase.rpc('search_groups', { p_query: query.trim() })
    setSearching(false)
    if (error) setError(error.message)
    else setHits((data as SearchHit[]) ?? [])
  }

  async function apply(g: SearchHit) {
    setBusy(true)
    setError(null)

    const { data, error } = await supabase.rpc('apply_to_group', { p_group_id: g.id })
    setBusy(false)
    if (error) {
      setError(error.message)
      return
    }

    setApplied(g.id)
    setHits((prev) => prev?.map((h) => (h.id === g.id ? { ...h, has_pending: true } : h)) ?? null)

    // Mejlet till ägaren går via workern — den har SendGrid-nyckeln. Går
    // anropet fel är det inte hela världen: ansökan syns ändå på ägarens
    // Gäng-sida, så felet sväljs medvetet.
    const base = import.meta.env.VITE_DATAGOLF_WORKER_URL
    if (base && data) {
      const { data: sess } = await supabase.auth.getSession()
      void fetch(`${base}/notify-application`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${sess.session?.access_token ?? ''}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ application_id: data as string }),
      }).catch(() => undefined)
    }

    await load()
  }

  async function decide(a: IncomingApplication, approve: boolean) {
    setBusy(true)
    setError(null)
    const { error } = await supabase.rpc('decide_group_application', {
      p_application_id: a.id,
      p_approve: approve,
    })
    setBusy(false)
    if (error) setError(error.message)
    await load()
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)

    const { error } = await supabase.rpc('create_group', {
      p_name: name.trim(),
      p_slug: slugify(name),
      p_password: password,
      p_code: code.trim(),
    })

    setBusy(false)
    if (error) {
      setError(error.message)
      return
    }

    setName('')
    setPassword('')
    setCode('')
    await load()
  }

  async function copyInvite(g: Group) {
    const url = `${window.location.origin}/ga-med/${g.invite_token}`
    await navigator.clipboard.writeText(url)
    setCopied(g.id)
    setTimeout(() => setCopied(null), 2500)
  }

  return (
    <div className="page stack">
      <div>
        <p className="eyebrow">Majortipset</p>
        <h1>Mina gäng</h1>
      </div>

      {error && <div className="notice notice--error">{error}</div>}
      {loading && <p className="muted">Hämtar…</p>}

      {/* Ansökningar till gäng man förvaltar — överst, för det är det enda
          här som väntar på ett beslut av en. */}
      {incoming.length > 0 && (
        <section className="card stack">
          <h2>Ansökningar att ta ställning till</h2>
          {incoming.map((a) => (
            <div key={a.id} className="stack" style={{ gap: 6 }}>
              <p>
                <strong>{a.applicant_name}</strong> vill gå med i <strong>{a.group_name}</strong>.
                {a.message && <span className="muted"> ”{a.message}”</span>}
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button className="primary" onClick={() => void decide(a, true)} disabled={busy}>
                  Godkänn
                </button>
                <button onClick={() => void decide(a, false)} disabled={busy}>
                  Avslå
                </button>
              </div>
            </div>
          ))}
        </section>
      )}

      {!loading && groups.length === 0 && (
        <div className="card">
          <p className="muted">
            Du är inte med i något gäng än. Skapa ett nedan, eller använd en
            inbjudningslänk du fått.
          </p>
        </div>
      )}

      {groups.map((g) => (
        <article key={g.id} className="card stack">
          <div>
            <h2>{g.name}</h2>
            <p className="muted">/{g.slug}</p>
          </div>

          {/* Standardgänget har ingen inbjudningslänk — dess invite är
              avstängd och det saknar lösenord. En knapp som kopierar en
              länk som inte fungerar är sämre än ingen knapp. */}
          {g.is_default ? (
            <p className="muted">
              Här hamnar alla som registrerar sig utan inbjudan, så att ingen står
              utan gäng. Får du en inbjudningslänk till ett annat gäng — och dess
              lösenord — flyttas du dit automatiskt, och lämnar det här.
            </p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button onClick={() => void copyInvite(g)}>
                  {copied === g.id ? 'Kopierad' : 'Kopiera inbjudningslänk'}
                </button>
              </div>
              <p className="muted">
                Länken räcker inte ensam — den som går med måste också kunna gängets
                lösenord. Skicka dem helst var för sig, och kom ihåg att lösenordet
                inte går att ändra i efterhand.
              </p>
            </>
          )}
        </article>
      ))}

      <section className="card stack">
        <h2>Hitta ett gäng</h2>
        <p className="muted">
          Sök på namnet och ansök — gängets admin får ett mejl och godkänner
          eller avslår. Har du fått en inbjudningslänk går den fortfarande
          snabbast.
        </p>

        <form onSubmit={search} style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Gängets namn"
            style={{ flex: '1 1 200px' }}
            aria-label="Sök gäng"
          />
          <button type="submit" disabled={searching || query.trim().length < 2}>
            {searching ? 'Söker…' : 'Sök'}
          </button>
        </form>

        {hits !== null && hits.length === 0 && (
          <p className="muted">Inget gäng matchar ”{query.trim()}”.</p>
        )}

        {hits?.map((h) => (
          <div
            key={h.id}
            style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
          >
            <span style={{ flex: '1 1 200px' }}>
              {h.name}
              <span className="board__player-meta">
                {' '}· {h.member_count === 1 ? '1 medlem' : `${h.member_count} medlemmar`}
              </span>
            </span>
            {h.is_member ? (
              <span className="tag tag--ok">Du är med</span>
            ) : h.has_pending ? (
              <span className="tag tag--wip">
                {applied === h.id ? 'Ansökan skickad' : 'Väntar på svar'}
              </span>
            ) : (
              <button onClick={() => void apply(h)} disabled={busy}>
                Ansök
              </button>
            )}
          </div>
        ))}
      </section>

      {myApplications.length > 0 && (
        <section className="card stack">
          <h2>Dina ansökningar</h2>
          {myApplications.map((a) => (
            <p key={a.id}>
              {a.group_name}
              <span
                className={`tag${a.status === 'approved' ? ' tag--ok' : a.status === 'pending' ? ' tag--wip' : ''}`}
                style={{ marginLeft: 10 }}
              >
                {APPLICATION_STATUS[a.status] ?? a.status}
              </span>
            </p>
          ))}
        </section>
      )}

      <form className="card stack" onSubmit={onCreate}>
        <h2>Skapa ett gäng</h2>

        <div>
          <label htmlFor="group-code">Kod från admin</label>
          <input
            id="group-code"
            type="text"
            required
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="XXXX-XXXX-XXXX"
            autoComplete="off"
            spellCheck={false}
          />
          <p className="muted">
            Gäng skapas inte fritt. Mejla{' '}
            <a href="mailto:scratch@majortipset.com">scratch@majortipset.com</a> så får du
            en kod. Den gäller en gång.
          </p>
        </div>

        <div>
          <label htmlFor="group-name">Namn</label>
          <input
            id="group-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Sidebet Loosers"
          />
          {name.trim() && <p className="muted">Adress: /{slugify(name)}</p>}
        </div>

        <div>
          <label htmlFor="group-password">Lösenord för att gå med</label>
          <input
            id="group-password"
            type="text"
            required
            minLength={4}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="minst fyra tecken"
          />
          <p className="muted">
            Delas med gänget. Det är ingen personlig inloggning — det skyddar bara
            mot att någon som råkar få länken kliver in.
          </p>
        </div>

        <button className="primary" type="submit" disabled={busy || !name.trim() || password.length < 4 || !code.trim()}>
          {busy ? 'Skapar…' : 'Skapa gäng'}
        </button>
      </form>
    </div>
  )
}
